import * as React from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

export interface BottomBarButtonProps
	extends Omit<React.ComponentPropsWithoutRef<typeof Link>, 'to'> {
	label: string
	icon: React.ReactNode
	currentPage?: string
	href?: string
	disabled?: boolean
}

const BottomBarButton = React.forwardRef<HTMLAnchorElement, BottomBarButtonProps>(
	({ label, icon, currentPage, href, disabled, ...props }, ref) => {
		const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
			if (disabled) {
				e.preventDefault()
				e.stopPropagation()
				return
			}
			// Call original onClick if provided
			if (props.onClick) {
				props.onClick(e)
			}
		}

		// Create a more robust matching system
		const isActive = () => {
			if (!currentPage || !href) return false

			// Extract the path from href (e.g., "/ai-agent" -> "ai-agent")
			const hrefPath = href.replace('/', '')

			// Check if currentPage matches the href path
			return currentPage === hrefPath
		}

		if (disabled) {
			return (
				<span
					className={cn(
						'flex gap-2 items-center justify-center p-1 z-20 rounded-lg transition-all cursor-not-allowed',
						'pointer-events-none opacity-50'
					)}
					aria-disabled={true}
					tabIndex={-1}
				>
					<span className="backface-hidden">{icon}</span>
				</span>
			)
		}

		return (
			<Link
				ref={ref}
				to={href || '#'}
				className={cn(
					'flex gap-2 p-1 z-20 rounded-lg transition-all cursor-pointer active:scale-100 hover:scale-105 transition-all duration-300 ease-in-out',
					isActive() && 'bg-purple-100 border border-purple-200 text-purple-700'
				)}
				onClick={handleClick}
				aria-current={isActive() ? 'page' : undefined}
				{...props}
			>
				<span className="backface-hidden">{icon}</span>
			</Link>
		)
	}
)

BottomBarButton.displayName = 'BottomBarButton'

export { BottomBarButton }
